import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Mail, MapPin, Menu, Phone } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import Logo from "./Logo";
import { useQuote } from "./QuoteProvider";
import { CONTACT, SEGMENTS, PCB_TYPES } from "@/data/site";

const ABOUT_LINKS = [
    { label: "Company Overview", href: "/about#overview" },
    { label: "Why Choose Us", href: "/about#why-us" },
    { label: "Who We Serve", href: "/about#who-we-serve" },
];

const INFRA_LINKS = [
    { label: "Manufacturing Capabilities", href: "/infrastructure#capabilities" },
    { label: "Production Process", href: "/infrastructure#production-process" },
    { label: "Quality Inspection & Testing", href: "/infrastructure#inspection-testing" },
    { label: "Packaging & Dispatch", href: "/infrastructure#packaging" },
];

const PCB_LINKS = [
    { label: "PCB Manufacturing Overview", href: "/pcb-manufacturing" },
    ...PCB_TYPES.map((t) => ({ label: t.name, href: `/pcb-manufacturing#${t.id}` })),
    { label: "PCB Photo Gallery", href: "/gallery" },
];

const NAV = [
    { label: "Home", href: "/" },
    { label: "About Us", href: "/about", links: ABOUT_LINKS },
    { label: "Market Segments", href: "/market-segments", links: SEGMENTS.map((s) => ({ label: s.name, href: `/market-segments#${s.id}` })) },
    { label: "Infrastructure", href: "/infrastructure", links: INFRA_LINKS },
    { label: "PCB Manufacturing", href: "/pcb-manufacturing", links: PCB_LINKS },
    { label: "Quality", href: "/quality" },
    { label: "Contact Us", href: "/contact" },
];

function DesktopDropdown({ item }) {
    const [open, setOpen] = useState(false);
    const wide = item.links.length > 4;
    return (
        <div
            className="relative h-full"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
        >
            <Link
                to={item.href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex h-full items-center gap-1.5 border-b-2 px-3 py-1 text-[13.5px] font-medium text-navy transition-colors hover:border-brand-red hover:text-brand-red lg:px-4"
            >
                {item.label}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </Link>
            {open && (
                <div
                    className={`absolute left-1/2 top-full z-50 -translate-x-1/2 border border-slate-200 bg-white shadow-[0_16px_40px_rgba(2,6,23,0.14)] ${
                        wide ? "grid w-[520px] grid-cols-2 gap-x-1 p-3" : "w-72 p-2"
                    }`}
                    data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                    {item.links.map((l) => (
                        <Link
                            key={l.href + l.label}
                            to={l.href}
                            onClick={() => setOpen(false)}
                            className="group flex items-center justify-between px-3 py-2.5 text-[13px] text-slate-600 transition-colors hover:bg-slate-50 hover:text-navy"
                        >
                            <span className="flex items-center gap-2.5">
                                <span className="h-1 w-1 bg-brand-red" />
                                {l.label}
                            </span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function MobileNav({ onNavigate }) {
    const [openGroup, setOpenGroup] = useState(null);
    return (
        <nav className="flex h-full flex-col gap-1 overflow-y-auto p-6" data-testid="mobile-nav">
            <SheetTitle className="mb-4 border-b border-slate-200 pb-4">
                <Logo />
            </SheetTitle>
            {NAV.map((item) =>
                item.links ? (
                    <div key={item.label} className="border-b border-slate-100">
                        <div className="flex items-center justify-between py-3">
                            <Link
                                to={item.href}
                                onClick={onNavigate}
                                className="text-[15px] font-semibold text-navy"
                                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {item.label}
                            </Link>
                            <button
                                onClick={() => setOpenGroup(openGroup === item.label ? null : item.label)}
                                aria-label={`Toggle ${item.label} submenu`}
                                data-testid={`mobile-nav-toggle-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="p-1 text-slate-500"
                            >
                                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${openGroup === item.label ? "rotate-180" : ""}`} />
                            </button>
                        </div>
                        {openGroup === item.label && (
                            <div className="pb-3">
                                {item.links.map((l) => (
                                    <Link
                                        key={l.href + l.label}
                                        to={l.href}
                                        onClick={onNavigate}
                                        className="block py-1.5 pl-4 text-[13px] text-slate-500"
                                    >
                                        {l.label}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <Link
                        key={item.label}
                        to={item.href}
                        onClick={onNavigate}
                        className="border-b border-slate-100 py-3 text-[15px] font-semibold text-navy"
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                        {item.label}
                    </Link>
                )
            )}
        </nav>
    );
}

export default function Header() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { openQuote } = useQuote();
    const { pathname } = useLocation();

    useEffect(() => {
        setMobileOpen(false);
    }, [pathname]);

    return (
        <header className="relative z-50">
            {/* Top utility bar */}
            <div className="hidden bg-navy-dark text-slate-300 md:block">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 font-mono text-[11px] tracking-wide sm:px-6 lg:px-8">
                    <div className="flex items-center gap-6" data-testid="topbar-contacts">
                        <a href={CONTACT.phone1Href} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="topbar-phone-1">
                            <Phone className="h-3 w-3 text-brand-red" /> {CONTACT.phone1}
                        </a>
                        <a href={CONTACT.phone2Href} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="topbar-phone-2">
                            <Phone className="h-3 w-3 text-brand-red" /> {CONTACT.phone2}
                        </a>
                        <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 transition-colors hover:text-white" data-testid="topbar-email">
                            <Mail className="h-3 w-3 text-brand-red" /> {CONTACT.email}
                        </a>
                    </div>
                    <p className="flex items-center gap-2 text-slate-400" data-testid="topbar-address">
                        <MapPin className="h-3 w-3 text-brand-red" /> Sahibabad, Ghaziabad, Uttar Pradesh, India
                    </p>
                </div>
            </div>

            {/* Main bar */}
            <div className="sticky top-0 z-50 border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
                    <Link to="/" data-testid="header-logo-link" aria-label="Unimicron Technology India - Home">
                        <Logo />
                    </Link>

                    <div className="hidden items-center gap-0 lg:flex lg:h-16">
                        <nav className="flex h-full items-stretch">
                            {NAV.map((item) =>
                                item.links ? (
                                    <DesktopDropdown key={item.label} item={item} />
                                ) : (
                                    <Link
                                        key={item.label}
                                        to={item.href}
                                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                                        className="flex h-full items-center border-b-2 border-transparent px-3 py-1 text-[13.5px] font-medium text-navy transition-colors hover:border-brand-red hover:text-brand-red lg:px-4"
                                    >
                                        {item.label}
                                    </Link>
                                )
                            )}
                        </nav>
                        <button
                            onClick={openQuote}
                            data-testid="header-get-quote-btn"
                            className="ml-5 border border-brand-red bg-brand-red px-5 py-2.5 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                        >
                            Get a Quote
                        </button>
                    </div>

                    {/* Mobile */}
                    <div className="flex items-center gap-3 lg:hidden">
                        <button
                            onClick={openQuote}
                            data-testid="header-get-quote-btn-mobile"
                            className="border border-brand-red bg-brand-red px-3.5 py-2 text-[12px] font-semibold uppercase tracking-wider text-white"
                        >
                            Get a Quote
                        </button>
                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <button
                                    aria-label="Open navigation menu"
                                    data-testid="mobile-menu-btn"
                                    className="flex h-10 w-10 items-center justify-center border border-slate-300 text-navy"
                                >
                                    <Menu className="h-5 w-5" />
                                </button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-[320px] bg-white p-0 sm:w-[360px]">
                                <MobileNav onNavigate={() => setMobileOpen(false)} />
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
}
